import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetBookingByIdQuery, useUpdateBookingMutation } from '../Service/bookingsApi';
import { useGetBookingOffersLookupQuery } from '../Service/offersLookupApi';
import { bookingFields } from '../fields';
import type { FormField } from '../../Shared/Components/FormRenderer';
import {
  BankDocumentsRoutes,
  BookingsRoutes,
  ExpenseInvoicesRoutes,
  ReportsRoutes,
  ReservationsRoutes,
} from '../../../Router/path';

import { bookingValues } from '../initialValues';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EntityFormLayout from './../../Shared/Components/EntityFormLayout/EntityFormLayout';
import { useGetBookingDiscountsLookupQuery } from '../Service/discountsLookupApi';
import { MultiAssign } from '../../Shared/Components/MultiAssign';
import {
  useAssignDiscountsToBookingMutation,
  useBatchCreateInvoicesForBookingMutation,
  useCancelBookingMutation,
  useCreateBookingSimplifiedCostMutation,
  useGetBookingWithDiscountsQuery,
  useGetBookingWithExpenseInvoicesQuery,
  useGetBookingWithReservationsQuery,
  useUpdateBookingExpenseInvoiceMappingDetailsMutation,
  type BatchInvoiceCreationResultDto,
} from '../Service/extendedBookingsApi';
import {
  useGetBookingExpenseInvoiceSuggestionsLookupQuery,
  useGetBookingExpenseInvoicesLookupQuery,
  useLazyGetBookingExpenseInvoiceDetailsLookupQuery,
  useSaveBookingExpenseInvoiceAssignmentsMutation,
} from '../Service/expenseInvoicesLookupApi';
import type {
  BookingExpenseInvoiceDetailsLookup,
  BookingExpenseInvoiceLookup,
  BookingExpenseInvoiceSuggestionLookup,
  SaveBookingExpenseInvoiceAssignmentLookup,
} from '../Service/expenseInvoicesLookupMapper';
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard';
import BookingSettlementPanel from '../Components/BookingSettlementPanel';
import BookingEventReportPanel from '../Components/BookingEventReportPanel';
import DiscountIcon from '@mui/icons-material/Discount';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SummarizeIcon from '@mui/icons-material/Summarize';
import BookingAssignedExpenseInvoicesTable from '../Components/BookingAssignedExpenseInvoicesTable';
import BookingExpenseInvoiceSuggestionsPanel from '../Components/BookingExpenseInvoiceSuggestionsPanel';
import BookingSimplifiedCostModal from '../Components/BookingSimplifiedCostModal';
import BatchInvoiceConfigModal from '../Components/BatchInvoiceConfigModal/BatchInvoiceConfigModal';
import BookingParticipantsTable from '../Components/BookingParticipantsTable';
import BookingReservationDataTable from '../Components/BookingReservationDataTable';
import Status from '../../Shared/Components/Status/Status';
import { Booking, BookingExpenseInvoiceMappingDetailItem } from '../../Shared/Types/bookingTypes';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import dayjs from 'dayjs';
import { DISPLAY_DATE_FORMAT } from '../../Shared/Helpers/dateHelpers';
import { CANCEL_BOOKING_CONFIRMATION_TEXT } from '../Service/bookingCancellationApi.contract';
import { BookingNavigationState, defaultBookingSearchFilters } from '../Types/booking.types';
import type {
  BookingReturnToReportNavigationState,
  UninvoicedReservationPaymentsReportRestoreNavigationState,
  UnsettledBookingsAccountingReportRestoreNavigationState,
} from '../../Shared/Types/reportsNavigationTypes';
import { useGetBookingExpenseInvoiceCompaniesLookupQuery } from '../Service/bookingExpenseInvoiceCompaniesLookupApi';
import { useGetBookingExpenseInvoiceTagsLookupQuery } from '../Service/bookingExpenseInvoiceTagsLookupApi';
import type {
  BookingSimplifiedCostErrorCategory,
} from '../Service/bookingSimplifiedCostErrorClassifier';
import {
  classifyBookingSimplifiedCostError,
  isRetryableBookingSimplifiedCostCategory,
} from '../Service/bookingSimplifiedCostErrorClassifier';
import {
  BookingSimplifiedCostSchemaMismatchError,
  parseCreateBookingSimplifiedCostResponse,
} from '../Service/bookingSimplifiedCostMapper';
import type { CreateBookingSimplifiedCostRequest } from '../Types/bookingSimplifiedCost.types';
import type { BatchInvoiceConfig } from '../Types/batchInvoiceConfigTypes';
import {
  emitBookingSimplifiedCostTelemetry,
  type BookingSimplifiedCostRetryMode,
} from '../../../Services/Telemetry/bookingSimplifiedCostTelemetry';
import { useSearchableBookingReservationPartiesLookupQuery } from '../Service/bookingReservationPartiesLookupApi';
import {
  createBookingReservationsPdfBlob,
  downloadBookingReservationsPdf,
} from '../Helpers/bookingReservationsPdfExport';
import {
  createBookingExpenseInvoicesPdfBlob,
  downloadBookingExpenseInvoicesPdf,
} from '../Helpers/bookingExpenseInvoicesPdfExport';
import { getBookingReservationDisplayPartyId } from '../Helpers/getBookingReservationApplicantDisplay';
import { useLazyGetInvoicesByReservationQuery } from '../../Invoices/Service/extendedInvoicesApi';
import { mapReservationInvoiceDtoToLookup } from '../../Reservation/Service/reservationInvoicesLookupMapper';
import type { BookingReservationInvoiceLookup } from '../Types/bookingReservationTable.types';

interface SelectedInvoiceAllocationSummary {
  currency: string;
  totalGross: number;
  totalAllocated: number;
  currentBookingAllocated: number;
  remainingToAllocate: number;
  remainingIncludingCurrentBooking: number;
}

interface BookingSimplifiedCostSubmitErrorState {
  category: BookingSimplifiedCostErrorCategory;
  message: string;
}

interface BookingSimplifiedCostRetryState {
  request: CreateBookingSimplifiedCostRequest;
  idempotencyKey: string;
  hasAutoRetryAlready: boolean;
}

const AUTO_RETRY_BACKOFF_MS = 350;

const createIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `booking-simplified-cost-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const parsePositiveIntegerQueryParam = (value: string | null): number | null => {
  if (!value) {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const normalizeBookingStatusNameForApi = (statusName?: string): string => {
  const trimmedStatusName = statusName?.trim() ?? '';
  if (!trimmedStatusName) {
    return '';
  }

  const statusNameMap: Record<string, string> = {
    Potwierdzona: 'Confirmed',
    Promocja: 'Promotion',
    'Na telefon': 'ByPhone',
    'Brak miejsc': 'SoldOut',
    'Ostatnie miejsca': 'LastPlaces',
  };

  return statusNameMap[trimmedStatusName] ?? trimmedStatusName;
};

const EditBooking = () => {
  const headerIcon = <BookOnlineIcon fontSize="large" color="primary" />;
  const sectionInfoIcon = <InfoIcon fontSize="small" color="primary" />;
  const sectionSettingsIcon = <SettingsIcon fontSize="small" color="primary" />;

  const bookingId = useParams<{ bookingId: string }>().bookingId;
  const parsedBookingId = bookingId ? parseInt(bookingId, 10) : 0;
  const hasValidBookingId = Number.isFinite(parsedBookingId) && parsedBookingId > 0;
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const navigationState = (location.state as BookingNavigationState | null) ?? null;
  const returnContext =
    navigationState?.returnContext?.pathname === BookingsRoutes.root &&
    navigationState.returnContext.filters
      ? navigationState.returnContext
      : null;
  const isBankDocumentsOrigin = searchParams.get('origin') === 'bank-documents';
  const returnToBankDocuments = useMemo(() => {
    const returnTo = searchParams.get('returnTo');

    return isBankDocumentsOrigin && returnTo === BankDocumentsRoutes.queue ? returnTo : null;
  }, [isBankDocumentsOrigin, searchParams]);
  const isUnsettledBookingsReportOrigin =
    searchParams.get('origin') === 'unsettled-bookings-report';
  const returnToUnsettledBookingsReport = useMemo(() => {
    const returnTo = searchParams.get('returnTo');

    return isUnsettledBookingsReportOrigin && returnTo === ReportsRoutes.unsettledBookingsAccounting
      ? returnTo
      : null;
  }, [isUnsettledBookingsReportOrigin, searchParams]);
  const isUninvoicedReservationPaymentsReportOrigin =
    searchParams.get('origin') === 'uninvoiced-reservation-payments-report';
  const returnToUninvoicedReservationPaymentsReport = useMemo(() => {
    const returnTo = searchParams.get('returnTo');

    return isUninvoicedReservationPaymentsReportOrigin &&
      returnTo === ReportsRoutes.uninvoicedReservationPayments
      ? returnTo
      : null;
  }, [isUninvoicedReservationPaymentsReportOrigin, searchParams]);
  const reportNavigationState = (location.state as BookingReturnToReportNavigationState | null) ?? null;
  // Each report reads only its own discriminated state field (origin URL selects which button/state
  // pair is active), so a state carried over from the other report's navigation is never applied here.
  const reportReturnContext = returnToUnsettledBookingsReport
    ? reportNavigationState?.reportReturnContext ?? null
    : null;
  const uninvoicedReportReturnContext = returnToUninvoicedReservationPaymentsReport
    ? reportNavigationState?.uninvoicedReportReturnContext ?? null
    : null;
  const focusedReservationId = useMemo(() => {
    if (!isBankDocumentsOrigin) {
      return null;
    }

    return parsePositiveIntegerQueryParam(searchParams.get('focusReservationId'));
  }, [isBankDocumentsOrigin, searchParams]);
  const {
    data: bookingDataById,
    error: bookingDataByIdError,
    isLoading: bookingDataByIdLoading,
  } = useGetBookingByIdQuery(parsedBookingId, { skip: !hasValidBookingId });
  const [cancelBooking, { isLoading: isCancellingBooking }] = useCancelBookingMutation();
  const cancelledAtUtc = bookingDataById?.cancelledAtUtc;
  const isBookingCancelled = Boolean(cancelledAtUtc);

  const handleCancelBooking = useCallback(async () => {
    if (!window.confirm(CANCEL_BOOKING_CONFIRMATION_TEXT)) {
      return;
    }

    try {
      await cancelBooking(parsedBookingId).unwrap();
      toast.success('Termin został anulowany.');
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Nie udało się anulować terminu.'));
    }
  }, [cancelBooking, parsedBookingId]);

  const headerActions = useMemo(() => {
    const actions: React.ReactNode[] = [];

    if (returnToBankDocuments) {
      actions.push(
        <Button
          key="bank-documents-return"
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={() => navigate(returnToBankDocuments)}
        >
          Wróć do dokumentów bankowych
        </Button>
      );
    }

    if (returnToUnsettledBookingsReport) {
      actions.push(
        <Button
          key="unsettled-bookings-report-return"
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={() =>
            navigate(returnToUnsettledBookingsReport, {
              state: reportReturnContext
                ? ({
                    restoredReportFilters: reportReturnContext,
                  } satisfies UnsettledBookingsAccountingReportRestoreNavigationState)
                : undefined,
            })
          }
        >
          Wróć do raportu
        </Button>
      );
    }

    if (returnToUninvoicedReservationPaymentsReport) {
      actions.push(
        <Button
          key="uninvoiced-reservation-payments-report-return"
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={() =>
            navigate(returnToUninvoicedReservationPaymentsReport, {
              state: uninvoicedReportReturnContext
                ? ({
                    restoredUninvoicedReportFilters: uninvoicedReportReturnContext,
                  } satisfies UninvoicedReservationPaymentsReportRestoreNavigationState)
                : undefined,
            })
          }
        >
          Wróć do raportu
        </Button>
      );
    }

    if (returnContext) {
      actions.push(
        <Button
          key="bookings-return"
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={() =>
            navigate(BookingsRoutes.root, {
              state: {
                restoredFilters: returnContext.filters ?? defaultBookingSearchFilters,
                restoredPage: returnContext.page ?? 0,
                restoredRowsPerPage: returnContext.rowsPerPage ?? 10,
              } satisfies BookingNavigationState,
            })
          }
        >
          {returnContext.label ?? 'Wróć do wyników wyszukiwania'}
        </Button>
      );
    }

    if (!isBookingCancelled) {
      actions.push(
        <Button
          key="cancel-booking"
          color="warning"
          size="small"
          startIcon={<EventBusyIcon fontSize="small" />}
          disabled={isCancellingBooking}
          onClick={() => void handleCancelBooking()}
        >
          Anuluj termin
        </Button>
      );
    }

    if (actions.length === 0) {
      return undefined;
    }

    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {actions}
      </Box>
    );
  }, [
    handleCancelBooking,
    isBookingCancelled,
    isCancellingBooking,
    navigate,
    returnContext,
    returnToBankDocuments,
    returnToUnsettledBookingsReport,
    reportReturnContext,
    returnToUninvoicedReservationPaymentsReport,
    uninvoicedReportReturnContext,
  ]);

  const { data: offers, isLoading: offersLoading } = useGetBookingOffersLookupQuery();
  const [updateBooking, statusUpdate] = useUpdateBookingMutation();
  const { data: allDiscounts, isLoading: allDiscountsLoading } = useGetBookingDiscountsLookupQuery();
  const [expenseInvoiceSearch, setExpenseInvoiceSearch] = useState('');
  const [debouncedExpenseInvoiceSearch, setDebouncedExpenseInvoiceSearch] = useState('');
  const { data: expenseInvoices, isLoading: isLoadingExpenseInvoicesList } =
    useGetBookingExpenseInvoicesLookupQuery({
      pageNumber: 1,
      pageSize: 50,
      searchTerm: debouncedExpenseInvoiceSearch || undefined,
    });
  const [getExpenseInvoiceById] = useLazyGetBookingExpenseInvoiceDetailsLookupQuery();
  const [assignBookingToExpenseInvoice, allocationMutationStatus] =
    useSaveBookingExpenseInvoiceAssignmentsMutation();
  const { refetch: refetchExpenseInvoiceSuggestions } = useGetBookingExpenseInvoiceSuggestionsLookupQuery(
    parsedBookingId,
    { skip: !hasValidBookingId }
  );
  const [pendingUnassignExpenseInvoiceId, setPendingUnassignExpenseInvoiceId] = useState<number | null>(
    null
  );
  const suggestionPrefillRef = useRef(false);
  const [createBookingSimplifiedCost, createBookingSimplifiedCostMutationState] =
    useCreateBookingSimplifiedCostMutation();

  const [updateMappingDetails] = useUpdateBookingExpenseInvoiceMappingDetailsMutation();
  const mappingEditsRef = useRef<BookingExpenseInvoiceMappingDetailItem[]>([]);
  const [mappingEdits, setMappingEdits] = useState<BookingExpenseInvoiceMappingDetailItem[]>([]);
  mappingEditsRef.current = mappingEdits;

  const handleMappingRowChange = useCallback((item: BookingExpenseInvoiceMappingDetailItem) => {
    setMappingEdits((prev) => [
      ...prev.filter((e) => e.expenseInvoiceId !== item.expenseInvoiceId),
      item,
    ]);
  }, []);

  const [selectedExpenseInvoice, setSelectedExpenseInvoice] =
    useState<BookingExpenseInvoiceLookup | null>(null);
  const [allocationAmount, setAllocationAmount] = useState<string>('');
  const [selectedAssignmentTagId, setSelectedAssignmentTagId] = useState<number | null | undefined>(
    null
  );
  const [allocationError, setAllocationError] = useState<string>('');
  const [allocationSuccess, setAllocationSuccess] = useState<string>('');
  const [isLoadingSelectedInvoiceSummary, setIsLoadingSelectedInvoiceSummary] = useState(false);
  const [selectedInvoiceSummaryError, setSelectedInvoiceSummaryError] = useState<string>('');
  const [selectedExpenseInvoiceDetails, setSelectedExpenseInvoiceDetails] =
    useState<BookingExpenseInvoiceDetailsLookup | null>(null);
  const [selectedInvoiceSummary, setSelectedInvoiceSummary] =
    useState<SelectedInvoiceAllocationSummary | null>(null);
  const [isSimplifiedCostModalOpen, setIsSimplifiedCostModalOpen] = useState(false);
  const [isAutoRetryingQuickCreate, setIsAutoRetryingQuickCreate] = useState(false);
  const [isQuickCreatePendingLock, setIsQuickCreatePendingLock] = useState(false);
  const [quickCreateErrorState, setQuickCreateErrorState] =
    useState<BookingSimplifiedCostSubmitErrorState | null>(null);
  const [quickCreateRetryState, setQuickCreateRetryState] =
    useState<BookingSimplifiedCostRetryState | null>(null);
  const [isExportingReservationsPdf, setIsExportingReservationsPdf] = useState(false);
  const [isExportingExpenseInvoicesPdf, setIsExportingExpenseInvoicesPdf] = useState(false);
  const isQuickCreateMountedRef = useRef(true);
  const isAssignmentTagDirtyRef = useRef(false);
  const autoRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRetryWaitResolverRef = useRef<((shouldContinue: boolean) => void) | null>(null);
  const [getInvoicesByReservation] = useLazyGetInvoicesByReservationQuery();

  const entityForm = useEntityForm<Booking>({
    initialData: bookingDataById ?? bookingValues,
    fields: bookingFields.map((field): FormField<Booking> => {
      if (field.type === 'autocomplete' && offers) {
        return {
          ...field,
          name: field.name as keyof Booking,
          options: offers.map((offer) => ({
            label: offer.name,
            value: offer.id,
          })),
          loading: offersLoading,
        };
      }

      if (field.name === 'bookingStatusName') {
        const current = bookingDataById?.bookingStatusName ?? '';
        const isDisabled = current !== '';
        const options = isDisabled
          ? [{ label: current, value: current }]
          : [
              { label: 'Potwierdzona', value: 'Confirmed' },
              { label: 'Brak', value: '' },
            ];

        return {
          ...field,
          name: 'bookingStatusName',
          label: field.label,
          value: current,
          options,
          disabled: isDisabled,
        };
      }

      return {
        ...field,
        name: field.name as keyof Booking,
      };
    }).map((field): FormField<Booking> => (isBookingCancelled ? { ...field, disabled: true } : field)),
    redirectUrl: BookingsRoutes.root,
    submitFn: async (data) => {
      const normalizedData: Booking = {
        ...data,
        bookingStatusName: normalizeBookingStatusNameForApi(data.bookingStatusName),
      };

      await updateBooking({ id: parsedBookingId, data: normalizedData }).unwrap();

      const currentEdits = mappingEditsRef.current;
      if (currentEdits.length > 0) {
        await updateMappingDetails({ bookingId: parsedBookingId, items: currentEdits }).unwrap();
      }
    },
    mutationStatus: statusUpdate,
    title: 'Edytuj rezerwację',
    icon: headerIcon,
    backUrl: BookingsRoutes.root,
    headerActions,
    groupOrder: [FormRenderGroupTypes.basic, FormRenderGroupTypes.configuration],
    groupIcons: {
      Podstawowe: sectionInfoIcon,
      Konfiguracja: sectionSettingsIcon,
    },
  });

  const { data: withDiscounts, isLoading: isLoadingWithDiscounts } =
    useGetBookingWithDiscountsQuery(parsedBookingId, {
      skip: !hasValidBookingId,
      refetchOnMountOrArgChange: true,
    });
  const {
    data: withExpenseInvoices,
    isLoading: isLoadingExpenseInvoices,
    refetch: refetchExpenseInvoicesForBooking,
  } = useGetBookingWithExpenseInvoicesQuery(parsedBookingId, {
    skip: !hasValidBookingId,
    refetchOnMountOrArgChange: true,
  });
  const {
    data: withReservations,
    isLoading: isLoadingReservations,
    refetch: refetchReservationsForBooking,
  } = useGetBookingWithReservationsQuery(parsedBookingId, {
    skip: !hasValidBookingId,
    refetchOnMountOrArgChange: true,
  });
  const shouldLoadQuickCreateLookups = isSimplifiedCostModalOpen;
  const {
    data: simplifiedCostCompanies = [],
    isLoading: isLoadingSimplifiedCostCompanies,
    isFetching: isFetchingSimplifiedCostCompanies,
  } = useGetBookingExpenseInvoiceCompaniesLookupQuery({
    skip: !shouldLoadQuickCreateLookups,
  });
  const {
    data: simplifiedCostTags = [],
    isLoading: isLoadingSimplifiedCostTags,
    isFetching: isFetchingSimplifiedCostTags,
  } = useGetBookingExpenseInvoiceTagsLookupQuery();
  const initialQuickCreateCompanyId = useMemo(() => {
    const offerId = bookingDataById?.offerId;
    if (typeof offerId !== 'number' || !Number.isInteger(offerId) || offerId <= 0 || !offers) {
      return null;
    }

    const matchedOffer = offers.find((offer) => offer.id === offerId);
    const companyId = matchedOffer?.companyId;
    return typeof companyId === 'number' && Number.isInteger(companyId) && companyId > 0
      ? companyId
      : null;
  }, [bookingDataById?.offerId, offers]);
  const isQuickCreateCompanySelectionLocked = initialQuickCreateCompanyId !== null;

  const [selected, setSelected] = useState<number[]>([]);
  const assignedExpenseInvoices = useMemo(
    () => withExpenseInvoices?.expenseInvoices ?? [],
    [withExpenseInvoices?.expenseInvoices]
  );
  const availableExpenseInvoices = expenseInvoices ?? [];
  const selectedAssignmentTag = useMemo(
    () => simplifiedCostTags.find((tag) => tag.id === selectedAssignmentTagId) ?? null,
    [selectedAssignmentTagId, simplifiedCostTags]
  );

  const selectedInvoiceAssignment = useMemo(() => {
    if (!selectedExpenseInvoice) {
      return null;
    }

    return (
      assignedExpenseInvoices.find(
        (invoice) => invoice.expenseInvoiceId === selectedExpenseInvoice.id
      ) ?? null
    );
  }, [selectedExpenseInvoice, assignedExpenseInvoices]);

  const totalPlaces =
    bookingDataById?.numberOfParticipants ?? withReservations?.numberOfParticipants ?? 0;

  const reservedPlaces = useMemo(() => {
    const reservations = withReservations?.reservations ?? [];

    return reservations.reduce((sum, reservation) => {
      const participantsCount =
        reservation.participantsCount ??
        reservation.numberOfParticipants ??
        reservation.participants?.length ??
        0;

      return sum + participantsCount;
    }, 0);
  }, [withReservations?.reservations]);

  const freePlaces = Math.max(
    withReservations?.freePlaces ?? bookingDataById?.freePlaces ?? totalPlaces - reservedPlaces,
    0
  );

  const freePlacesChipColor: 'success' | 'warning' | 'error' =
    freePlaces === 0 ? 'error' : freePlaces <= 3 ? 'warning' : 'success';

  const allParticipants = useMemo(() => {
    const reservations = withReservations?.reservations ?? [];

    return reservations.flatMap((reservation) => reservation.participants ?? []);
  }, [withReservations?.reservations]);
  const reservationsForBooking = useMemo(
    () => withReservations?.reservations ?? [],
    [withReservations?.reservations]
  );
  const reservationDisplayPartyIds = useMemo(
    () =>
      Array.from(
        new Set(
          reservationsForBooking
            .map((reservation) => getBookingReservationDisplayPartyId(reservation))
            .filter((id): id is number => typeof id === 'number')
        )
      ),
    [reservationsForBooking]
  );
  const { parties: reservationParties } = useSearchableBookingReservationPartiesLookupQuery(
    reservationDisplayPartyIds.length > 0
      ? { ids: reservationDisplayPartyIds, take: reservationDisplayPartyIds.length }
      : undefined
  );

  useEffect(() => {
    if (withDiscounts) {
      setSelected(withDiscounts.discounts?.map((d: { id: number }) => d.id) ?? []);
    }
  }, [withDiscounts]);

  useEffect(() => () => {
    isQuickCreateMountedRef.current = false;

    if (autoRetryTimeoutRef.current) {
      clearTimeout(autoRetryTimeoutRef.current);
      autoRetryTimeoutRef.current = null;
    }

    if (autoRetryWaitResolverRef.current) {
      autoRetryWaitResolverRef.current(false);
      autoRetryWaitResolverRef.current = null;
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedExpenseInvoiceSearch(expenseInvoiceSearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [expenseInvoiceSearch]);

  useEffect(() => {
    if (!selectedExpenseInvoice) {
      setAllocationAmount('');
      setSelectedAssignmentTagId(null);
      isAssignmentTagDirtyRef.current = false;
      setSelectedExpenseInvoiceDetails(null);
      setSelectedInvoiceSummary(null);
      setSelectedInvoiceSummaryError('');
      return;
    }

    const existingAssignment = assignedExpenseInvoices.find(
      (invoice) => invoice.expenseInvoiceId === selectedExpenseInvoice.id
    );

    const existingAssignmentTagId =
      existingAssignment === undefined
        ? null
        : Object.prototype.hasOwnProperty.call(existingAssignment, 'assignmentTagId')
          ? existingAssignment.assignmentTagId ?? null
          : undefined;

    setAllocationAmount(existingAssignment ? existingAssignment.allocatedAmount.toString() : '');
    if (!isAssignmentTagDirtyRef.current) {
      setSelectedAssignmentTagId(existingAssignmentTagId);
    }
  }, [selectedExpenseInvoice, assignedExpenseInvoices]);

  useEffect(() => {
    let isCancelled = false;

    const loadSelectedInvoiceSummary = async () => {
      if (!selectedExpenseInvoice) {
        return;
      }

      const isSuggestionSelection = suggestionPrefillRef.current;
      suggestionPrefillRef.current = false;

      try {
        setIsLoadingSelectedInvoiceSummary(true);
        setSelectedInvoiceSummaryError('');

        const expenseInvoiceDetails = await getExpenseInvoiceById(
          selectedExpenseInvoice.id
        ).unwrap();

        if (isCancelled) {
          return;
        }

        setSelectedExpenseInvoiceDetails(expenseInvoiceDetails);

        const bookings = expenseInvoiceDetails.bookings ?? [];
        const totalAllocated = bookings.reduce((sum, booking) => sum + booking.allocatedAmount, 0);
        const currentBooking = bookings.find((booking) => booking.bookingId === parsedBookingId);
        const currentBookingAllocated = currentBooking?.allocatedAmount ?? 0;
        const currentBookingAssignmentTagId =
          currentBooking === undefined
            ? null
            : Object.prototype.hasOwnProperty.call(currentBooking, 'assignmentTagId')
              ? currentBooking.assignmentTagId ?? null
              : undefined;
        const remainingToAllocate = Math.max(expenseInvoiceDetails.totalGross - totalAllocated, 0);
        if (!isAssignmentTagDirtyRef.current) {
          setSelectedAssignmentTagId(currentBookingAssignmentTagId);
        }

        if (isSuggestionSelection) {
          const remainingMagnitude = Math.max(
            Math.abs(expenseInvoiceDetails.totalGross) - Math.abs(totalAllocated),
            0
          );
          const proposedAmount =
            expenseInvoiceDetails.totalGross < 0 ? -remainingMagnitude : remainingMagnitude;
          setAllocationAmount(proposedAmount.toString());
        }

        setSelectedInvoiceSummary({
          currency: expenseInvoiceDetails.currency,
          totalGross: expenseInvoiceDetails.totalGross,
          totalAllocated,
          currentBookingAllocated,
          remainingToAllocate,
          remainingIncludingCurrentBooking: remainingToAllocate + currentBookingAllocated,
        });
      } catch (error) {
        if (!isCancelled) {
          setSelectedExpenseInvoiceDetails(null);
          setSelectedInvoiceSummary(null);
          setSelectedInvoiceSummaryError(
            extractApiErrorMessage(
              error,
              'Nie udało się pobrać podsumowania alokacji dla wybranego dokumentu kosztowego.'
            )
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingSelectedInvoiceSummary(false);
        }
      }
    };

    loadSelectedInvoiceSummary();

    return () => {
      isCancelled = true;
    };
  }, [getExpenseInvoiceById, parsedBookingId, selectedExpenseInvoice]);

  const handleSelectSuggestedExpenseInvoice = useCallback(
    (suggestion: BookingExpenseInvoiceSuggestionLookup) => {
      const lookup: BookingExpenseInvoiceLookup = {
        id: suggestion.id,
        documentKind: null,
        documentReference: suggestion.documentReference,
        displayIssuerName: suggestion.displayIssuerName,
        description: null,
        tags: [],
        invoiceNumber: suggestion.documentReference,
        supplierName: suggestion.displayIssuerName,
        currency: suggestion.currency,
        totalGross: suggestion.totalGross,
      };

      suggestionPrefillRef.current = true;
      setSelectedExpenseInvoice(lookup);
      isAssignmentTagDirtyRef.current = false;
      setAllocationError('');
      setAllocationSuccess('');
    },
    []
  );

  const [
    assign,
    { isLoading: isAssigning, isError: isErrorAssigning, isSuccess: isSuccessAssigning },
  ] = useAssignDiscountsToBookingMutation();

  const {
    isLoading: isSavingAllocation,
    isError: isErrorSavingAllocation,
    isSuccess: isSuccessSavingAllocation,
  } = allocationMutationStatus;
  const isCreatingBookingSimplifiedCost =
    createBookingSimplifiedCostMutationState.isLoading ||
    isAutoRetryingQuickCreate ||
    isQuickCreatePendingLock;
  const canRetryQuickCreateManually = Boolean(
    quickCreateErrorState &&
      isRetryableBookingSimplifiedCostCategory(quickCreateErrorState.category) &&
      quickCreateRetryState
  );

  const resetAssignmentEditorState = () => {
    setSelectedExpenseInvoice(null);
    setAllocationAmount('');
    setSelectedAssignmentTagId(null);
    isAssignmentTagDirtyRef.current = false;
    setSelectedExpenseInvoiceDetails(null);
    setSelectedInvoiceSummary(null);
    setSelectedInvoiceSummaryError('');
    setExpenseInvoiceSearch('');
    setDebouncedExpenseInvoiceSearch('');
  };

  const resolveSelectedExpenseInvoiceDetails = async (): Promise<BookingExpenseInvoiceDetailsLookup | null> => {
    if (!selectedExpenseInvoice) {
      return null;
    }

    if (selectedExpenseInvoiceDetails && selectedExpenseInvoiceDetails.id === selectedExpenseInvoice.id) {
      return selectedExpenseInvoiceDetails;
    }

    const freshDetails = await getExpenseInvoiceById(selectedExpenseInvoice.id).unwrap();
    setSelectedExpenseInvoiceDetails(freshDetails);
    return freshDetails;
  };

  const handleClearAssignmentSelection = () => {
    resetAssignmentEditorState();
    setAllocationError('');
    setAllocationSuccess('');
  };

  const handleSaveExpenseInvoiceAssignment = async () => {
    if (!selectedExpenseInvoice) {
      setAllocationError('Wybierz dokument kosztowy.');
      setAllocationSuccess('');
      return;
    }

    const parsedAmount = Number(allocationAmount);
    if (!Number.isFinite(parsedAmount)) {
      setAllocationError('Nieprawidłowa kwota przypisania.');
      setAllocationSuccess('');
      return;
    }

    try {
      setAllocationError('');
      setAllocationSuccess('');

      const expenseInvoiceDetails = await resolveSelectedExpenseInvoiceDetails();
      if (!expenseInvoiceDetails) {
        setAllocationError('Nie udało się pobrać danych wybranego dokumentu kosztowego.');
        return;
      }

      const existingAssignments: SaveBookingExpenseInvoiceAssignmentLookup[] = (
        expenseInvoiceDetails.bookings ?? []
      ).map((booking) => ({
        bookingId: booking.bookingId,
        allocatedAmount: booking.allocatedAmount,
        ...(booking.assignmentTagId !== undefined ? { tagId: booking.assignmentTagId } : {}),
      }));

      const mergedAssignments: SaveBookingExpenseInvoiceAssignmentLookup[] = [
        ...existingAssignments.filter((assignment) => assignment.bookingId !== parsedBookingId),
        {
          bookingId: parsedBookingId,
          allocatedAmount: parsedAmount,
          ...(selectedAssignmentTagId !== undefined ? { tagId: selectedAssignmentTagId } : {}),
        },
      ];

      const totalAllocated = mergedAssignments.reduce(
        (sum, assignment) => sum + assignment.allocatedAmount,
        0
      );

      if (Math.abs(totalAllocated) > Math.abs(expenseInvoiceDetails.totalGross)) {
        setAllocationError(
          `Suma alokacji (${totalAllocated.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${expenseInvoiceDetails.currency}) przekracza wartość dokumentu (${expenseInvoiceDetails.totalGross.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${expenseInvoiceDetails.currency}).`
        );
        return;
      }

      await assignBookingToExpenseInvoice({
        invoiceId: selectedExpenseInvoice.id,
        assignments: mergedAssignments,
      }).unwrap();

      await refetchExpenseInvoicesForBooking();
      refetchExpenseInvoiceSuggestions();
      resetAssignmentEditorState();
      setAllocationSuccess('Przypisanie dokumentu kosztowego zostało zapisane.');
    } catch (error) {
      setAllocationError(
        extractApiErrorMessage(error, 'Nie udało się zapisać przypisania dokumentu kosztowego.')
      );
      setAllocationSuccess('');
    }
  };

  const handleUnassignExpenseInvoiceFromTable = async (expenseInvoiceId: number) => {
    setPendingUnassignExpenseInvoiceId(expenseInvoiceId);

    try {
      const expenseInvoiceDetails = await getExpenseInvoiceById(expenseInvoiceId).unwrap();

      const existingAssignments: SaveBookingExpenseInvoiceAssignmentLookup[] = (
        expenseInvoiceDetails.bookings ?? []
      ).map((booking) => ({
        bookingId: booking.bookingId,
        allocatedAmount: booking.allocatedAmount,
        ...(booking.assignmentTagId !== undefined ? { tagId: booking.assignmentTagId } : {}),
      }));

      const remainingAssignments = existingAssignments.filter(
        (assignment) => assignment.bookingId !== parsedBookingId
      );

      await assignBookingToExpenseInvoice({
        invoiceId: expenseInvoiceId,
        assignments: remainingAssignments,
      }).unwrap();

      // PRF-007: the mutation only invalidates tags for bookingIds present in
      // `remainingAssignments`, which no longer includes this booking after unassign —
      // so this booking's own costs and suggestions must be refetched explicitly.
      await refetchExpenseInvoicesForBooking();
      refetchExpenseInvoiceSuggestions();
      toast.success('Przypisanie dokumentu kosztowego zostało odpięte od bookingu.');
    } catch (error) {
      toast.error(
        extractApiErrorMessage(error, 'Nie udało się odpiąć dokumentu kosztowego od bookingu.')
      );
    } finally {
      setPendingUnassignExpenseInvoiceId(null);
    }
  };

  const handleOpenSimplifiedCostModal = () => {
    setIsSimplifiedCostModalOpen(true);
    setQuickCreateErrorState(null);
    setQuickCreateRetryState(null);
  };

  const handleCloseSimplifiedCostModal = () => {
    if (isCreatingBookingSimplifiedCost) {
      return;
    }

    setIsSimplifiedCostModalOpen(false);
    setQuickCreateErrorState(null);
    setQuickCreateRetryState(null);
  };

  const handleFallbackToExpenseInvoices = () => {
    navigate(`${ExpenseInvoicesRoutes.create}?documentKind=simplifiedCost`);
  };

  const waitForQuickCreateAutoRetryBackoff = () =>
    new Promise<boolean>((resolve) => {
      if (!isQuickCreateMountedRef.current) {
        resolve(false);
        return;
      }

      if (autoRetryTimeoutRef.current) {
        clearTimeout(autoRetryTimeoutRef.current);
        autoRetryTimeoutRef.current = null;
      }

      autoRetryWaitResolverRef.current = resolve;
      autoRetryTimeoutRef.current = setTimeout(() => {
        autoRetryTimeoutRef.current = null;
        autoRetryWaitResolverRef.current = null;
        resolve(isQuickCreateMountedRef.current);
      }, AUTO_RETRY_BACKOFF_MS);
    });

  const runBookingSimplifiedCostQuickCreate = async (
    request: CreateBookingSimplifiedCostRequest,
    retryStateOverride?: BookingSimplifiedCostRetryState,
    retryMode?: BookingSimplifiedCostRetryMode
  ) => {
    if (retryMode) {
      emitBookingSimplifiedCostTelemetry('booking_simplified_cost_retry_triggered', {
        bookingId: parsedBookingId,
        retryMode,
      });
    } else {
      emitBookingSimplifiedCostTelemetry('booking_simplified_cost_submit_started', {
        bookingId: parsedBookingId,
      });
    }

    const retryState: BookingSimplifiedCostRetryState =
      retryStateOverride ?? {
        request,
        idempotencyKey: createIdempotencyKey(),
        hasAutoRetryAlready: false,
      };

    try {
      setQuickCreateErrorState(null);
      setQuickCreateRetryState(null);
      const response = await createBookingSimplifiedCost({
        bookingId: parsedBookingId,
        data: retryState.request,
        idempotencyKey: retryState.idempotencyKey,
      }).unwrap();
      const parsedResponse = parseCreateBookingSimplifiedCostResponse(response);

      if (!isQuickCreateMountedRef.current) {
        return;
      }

      emitBookingSimplifiedCostTelemetry('booking_simplified_cost_submit_succeeded', {
        bookingId: parsedBookingId,
      });

      toast.success(
        `Koszt uproszczony #${parsedResponse.expenseInvoiceId} został utworzony i przypisany do bookingu.`
      );
      setIsSimplifiedCostModalOpen(false);
      setQuickCreateErrorState(null);
      setQuickCreateRetryState(null);
    } catch (error) {
      const classifiedError = classifyBookingSimplifiedCostError(error);

      emitBookingSimplifiedCostTelemetry('booking_simplified_cost_submit_failed', {
        bookingId: parsedBookingId,
        category: classifiedError.category,
      });

      if (
        isRetryableBookingSimplifiedCostCategory(classifiedError.category) &&
        !retryState.hasAutoRetryAlready
      ) {
        if (!isQuickCreateMountedRef.current) {
          return;
        }

        setIsAutoRetryingQuickCreate(true);
        try {
          const shouldContinue = await waitForQuickCreateAutoRetryBackoff();
          if (!shouldContinue) {
            return;
          }

          await runBookingSimplifiedCostQuickCreate(
            retryState.request,
            { ...retryState, hasAutoRetryAlready: true },
            'auto'
          );
          return;
        } finally {
          if (isQuickCreateMountedRef.current) {
            setIsAutoRetryingQuickCreate(false);
          }
        }
      }

      if (!isQuickCreateMountedRef.current) {
        return;
      }

      if (error instanceof BookingSimplifiedCostSchemaMismatchError) {
        setQuickCreateErrorState({
          category: 'schemaMismatch',
          message: classifiedError.message,
        });
      } else {
        setQuickCreateErrorState(classifiedError);
      }
      setQuickCreateRetryState(retryState);
    }
  };

  const handleCreateBookingSimplifiedCost = async (request: CreateBookingSimplifiedCostRequest) => {
    if (isQuickCreatePendingLock) {
      return;
    }

    setIsQuickCreatePendingLock(true);
    try {
      await runBookingSimplifiedCostQuickCreate(request);
    } finally {
      if (isQuickCreateMountedRef.current) {
        setIsQuickCreatePendingLock(false);
      }
    }
  };

  const handleManualRetryBookingSimplifiedCost = async () => {
    if (!quickCreateRetryState || isQuickCreatePendingLock) {
      return;
    }

    setIsQuickCreatePendingLock(true);
    try {
      await runBookingSimplifiedCostQuickCreate(
        quickCreateRetryState.request,
        quickCreateRetryState,
        'manual'
      );
    } finally {
      if (isQuickCreateMountedRef.current) {
        setIsQuickCreatePendingLock(false);
      }
    }
  };

  const [batchSelectedReservationIds, setBatchSelectedReservationIds] = useState<number[]>([]);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [batchCreateInvoices, { isLoading: isBatchCreating }] =
    useBatchCreateInvoicesForBookingMutation();

  const batchItemDescriptionPrefill = useMemo(() => {
    const offerName = bookingDataById?.offerName?.trim();
    const fmt = (d?: string) => {
      if (!d) return undefined;
      const p = new Date(d);
      return Number.isNaN(p.getTime()) ? undefined : new Intl.DateTimeFormat('pl-PL').format(p);
    };
    const dateFrom = fmt(bookingDataById?.bookingDateFrom);
    const dateTo = fmt(bookingDataById?.bookingDateTo);
    return offerName && dateFrom && dateTo ? `${offerName} ${dateFrom} - ${dateTo}` : undefined;
  }, [bookingDataById]);

  const handleBatchModalConfirm = async (config: BatchInvoiceConfig) => {
    if (!parsedBookingId || batchSelectedReservationIds.length === 0) return;
    setIsBatchModalOpen(false);
    try {
      const results: BatchInvoiceCreationResultDto[] = await batchCreateInvoices({
        bookingId: parsedBookingId,
        reservationIds: batchSelectedReservationIds,
        ...config,
      }).unwrap();
      const succeeded = results.filter((r) => r.success);
      const failed = results.filter((r) => !r.success);
      if (failed.length === 0) {
        toast.success(`Wystawiono ${succeeded.length} z ${results.length} faktur.`);
      } else {
        const errorDetails = failed.map((r) => `Rez. ${r.reservationId}: ${r.error}`).join('; ');
        toast.warning(
          `Wystawiono ${succeeded.length} z ${results.length} faktur. Błędy: ${errorDetails}`
        );
      }
    } catch (error) {
      toast.error(extractApiErrorMessage(error, 'Nie udało się wystawić faktur.'));
    }
  };

  const handleAddReservation = () => {
    if (freePlaces <= 0 || isBookingCancelled) {
      return;
    }

    const returnTo = BookingsRoutes.update.replace(':bookingId', String(parsedBookingId));
    navigate(
      `${ReservationsRoutes.create}?bookingId=${parsedBookingId}&returnTo=${encodeURIComponent(returnTo)}`
    );
  };

  const handleDownloadReservationsPdf = async () => {
    if (reservationsForBooking.length === 0) {
      toast.error('Brak rezerwacji do eksportu.');
      return;
    }

    setIsExportingReservationsPdf(true);

    try {
      const invoicesByReservationEntries = await Promise.all(
        reservationsForBooking.map(async (reservation) => {
          const invoiceDtos = await getInvoicesByReservation(reservation.id).unwrap();
          const invoices: BookingReservationInvoiceLookup[] = (invoiceDtos ?? [])
            .map((invoice) => mapReservationInvoiceDtoToLookup(invoice))
            .filter((item): item is NonNullable<typeof item> => item !== undefined)
            .map((invoice) => ({
              id: invoice.id,
              number: invoice.number,
              typeLabel: invoice.typeLabel,
              sortOrder: invoice.sortOrder,
              issueDate: invoice.issueDate,
              totalGross: invoice.totalGross,
              currency: invoice.currency,
              sourceType: 'invoice-api',
            }));

          return [reservation.id, invoices] as const;
        })
      );

      const invoicesByReservationId = Object.fromEntries(invoicesByReservationEntries) as Record<
        number,
        BookingReservationInvoiceLookup[]
      >;
      const offerName = bookingDataById?.offerName ?? reservationsForBooking[0]?.offerName;
      const dateFrom = bookingDataById?.bookingDateFrom ?? reservationsForBooking[0]?.dateFrom;
      const dateTo = bookingDataById?.bookingDateTo ?? reservationsForBooking[0]?.dateTo;
      const pdfBlob = await createBookingReservationsPdfBlob({
        reservations: reservationsForBooking,
        parties: reservationParties,
        invoicesByReservationId,
        offerName,
        dateFrom,
        dateTo,
      });

      downloadBookingReservationsPdf(pdfBlob, `Rezerwacje_booking_${parsedBookingId}.pdf`);
    } catch (error) {
      const fallbackMessage = 'Nie udało się wygenerować PDF rezerwacji.';
      const message = error instanceof Error && error.message ? error.message : fallbackMessage;
      toast.error(message);
    } finally {
      setIsExportingReservationsPdf(false);
    }
  };

  const handleDownloadExpenseInvoicesPdf = async () => {
    if (assignedExpenseInvoices.length === 0) {
      toast.error('Brak przypisanych dokumentów kosztowych do eksportu.');
      return;
    }

    setIsExportingExpenseInvoicesPdf(true);
    try {
      const offerName = bookingDataById?.offerName;
      const dateFrom = bookingDataById?.bookingDateFrom;
      const dateTo = bookingDataById?.bookingDateTo;
      const pdfBlob = await createBookingExpenseInvoicesPdfBlob({
        expenseInvoices: assignedExpenseInvoices,
        offerName,
        dateFrom,
        dateTo,
      });
      downloadBookingExpenseInvoicesPdf(pdfBlob, `Koszty_booking_${parsedBookingId}.pdf`);
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : 'Nie udało się wygenerować PDF dokumentów kosztowych.';
      toast.error(message);
    } finally {
      setIsExportingExpenseInvoicesPdf(false);
    }
  };

  if (bookingDataByIdLoading) {
    return (
      <Typography align="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Typography>
    );
  }

  if (!hasValidBookingId || bookingDataByIdError) {
    return (
      <Typography color="error" align="center" sx={{ mt: 4 }}>
        Nie udało się załadować danych bookingu.
      </Typography>
    );
  }

  return (
    <EntityFormLayout {...entityForm}>
      {cancelledAtUtc && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Termin został anulowany. Data anulowania: {dayjs(cancelledAtUtc).format(DISPLAY_DATE_FORMAT)}.
        </Alert>
      )}
      <AccordionCard
        title="Przypisz zniżki do rezerwacji"
        icon={<DiscountIcon fontSize="small" color="primary" />}
      >
        <MultiAssign
          options={allDiscounts || []}
          loadingOptions={allDiscountsLoading || isLoadingWithDiscounts}
          selectedIds={selected}
          onChange={setSelected}
          getOptionLabel={(d) => d.name}
          getOptionValue={(d) => d.id}
          onSave={async () => {
            await assign({ bookingId: parsedBookingId, discountIds: selected }).unwrap();
          }}
          saving={isAssigning}
          label="Wybierz zniżki"
          placeholder="Zacznij pisać…"
        />
        <Status isLoading={isAssigning} isError={isErrorAssigning} isSuccess={isSuccessAssigning} />
      </AccordionCard>

      <Box sx={{ mt: 3 }}>
        <AccordionCard
          title="Przypisane Dokumenty Kosztowe"
          icon={<ReceiptIcon fontSize="small" color="primary" />}
          defaultExpanded
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Dokumenty kosztowe przypisane do tego bookingu (zapisywane w trybie
            merge-before-save).
          </Typography>

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => void handleDownloadExpenseInvoicesPdf()}
              disabled={isExportingExpenseInvoicesPdf || assignedExpenseInvoices.length === 0}
            >
              {isExportingExpenseInvoicesPdf ? 'Generowanie PDF...' : 'Pobierz PDF'}
            </Button>
            <Button
              variant="contained"
              onClick={handleOpenSimplifiedCostModal}
              disabled={isCreatingBookingSimplifiedCost}
            >
              Dodaj koszt uproszczony
            </Button>
          </Box>

          <BookingExpenseInvoiceSuggestionsPanel
            bookingId={parsedBookingId}
            onSelectDocument={handleSelectSuggestedExpenseInvoice}
          />

          <BookingAssignedExpenseInvoicesTable
            expenseInvoices={assignedExpenseInvoices}
            isLoading={isLoadingExpenseInvoices}
            editable
            onRowChange={handleMappingRowChange}
            onUnassign={handleUnassignExpenseInvoiceFromTable}
            pendingUnassignExpenseInvoiceId={pendingUnassignExpenseInvoiceId}
          />

          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Rekomendacja operacyjna: przypisania do dokumentów kosztowych powinny być edytowane
              przez jedną osobę naraz.
            </Alert>

            <Autocomplete
              options={availableExpenseInvoices}
              loading={isLoadingExpenseInvoicesList}
              value={selectedExpenseInvoice}
              filterOptions={(x) => x}
              onInputChange={(_, newValue, reason) => {
                if (reason === 'input') {
                  setExpenseInvoiceSearch(newValue);
                } else if (reason === 'clear') {
                  setExpenseInvoiceSearch('');
                  setDebouncedExpenseInvoiceSearch('');
                }
              }}
              onChange={(_, value) => {
                setSelectedExpenseInvoice(value);
                isAssignmentTagDirtyRef.current = false;
                setAllocationError('');
                setAllocationSuccess('');
              }}
              getOptionLabel={(option: BookingExpenseInvoiceLookup) =>
                `${option.documentReference} - ${option.displayIssuerName}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dokument kosztowy"
                  placeholder="Zacznij pisać nazwę dostawcy lub numer…"
                />
              )}
            />

            {selectedExpenseInvoice && (
              <>
                <Alert severity="info">
                  Kwota brutto wybranego dokumentu:{' '}
                  {selectedExpenseInvoice.totalGross.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                  {selectedExpenseInvoice.currency}
                </Alert>

                {isLoadingSelectedInvoiceSummary && (
                  <Typography variant="body2" color="text.secondary">
                    Ładowanie podsumowania alokacji...
                  </Typography>
                )}

                {!isLoadingSelectedInvoiceSummary && selectedInvoiceSummary && (
                  <>
                    <Alert severity="info">
                      Alokacja globalna dokumentu:{' '}
                      {selectedInvoiceSummary.totalAllocated.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                      {selectedInvoiceSummary.currency}{' '}
                      /{' '}
                      {selectedInvoiceSummary.totalGross.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                      {selectedInvoiceSummary.currency}
                    </Alert>
                    <Alert severity="info">
                      Pozostało do alokacji (globalnie):{' '}
                      {selectedInvoiceSummary.remainingToAllocate.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                      {selectedInvoiceSummary.currency}
                    </Alert>
                    {selectedInvoiceSummary.currentBookingAllocated > 0 ? (
                      <Alert severity="success">
                        Ten dokument ma już alokację dla tego bookingu:{' '}
                        {selectedInvoiceSummary.currentBookingAllocated.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        {selectedInvoiceSummary.currency}
                      </Alert>
                    ) : (
                      <Alert severity="warning">
                        Ten dokument nie ma jeszcze alokacji dla tego bookingu.
                      </Alert>
                    )}
                    <Alert severity="info">
                      Maksymalna kwota możliwa do ustawienia dla tego bookingu:{' '}
                      {selectedInvoiceSummary.remainingIncludingCurrentBooking.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                      {selectedInvoiceSummary.currency}
                    </Alert>
                  </>
                )}

                {!isLoadingSelectedInvoiceSummary && selectedInvoiceSummaryError && (
                  <Alert severity="error">{selectedInvoiceSummaryError}</Alert>
                )}

                {!isLoadingSelectedInvoiceSummary &&
                  !selectedInvoiceSummary &&
                  !selectedInvoiceSummaryError &&
                  (selectedInvoiceAssignment ? (
                    <Alert severity="success">
                      Ten dokument ma już alokację dla tego bookingu:{' '}
                      {selectedInvoiceAssignment.allocatedAmount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                      {selectedInvoiceAssignment.currency ?? selectedExpenseInvoice?.currency ?? 'PLN'}
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      Ten dokument nie ma jeszcze alokacji dla tego bookingu.
                    </Alert>
                  ))}
              </>
            )}

            <Autocomplete
              options={simplifiedCostTags}
              loading={isLoadingSimplifiedCostTags || isFetchingSimplifiedCostTags}
              value={selectedAssignmentTag}
              onChange={(_, value) => {
                isAssignmentTagDirtyRef.current = true;
                setSelectedAssignmentTagId(value?.id ?? null);
                setAllocationError('');
                setAllocationSuccess('');
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Rodzaj wydatku (opcjonalnie)"
                  placeholder="Wybierz tag przypisania dla tego bookingu"
                />
              )}
            />

            <TextField
              label="Kwota przypisana do tego bookingu"
              type="number"
              value={allocationAmount}
              onChange={(event) => {
                setAllocationAmount(event.target.value);
                setAllocationError('');
                setAllocationSuccess('');
              }}
              inputProps={{ step: 0.01 }}
            />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleSaveExpenseInvoiceAssignment}
                disabled={isSavingAllocation || !selectedExpenseInvoice}
              >
                Zapisz przypisanie
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<FilterAltOffIcon />}
                onClick={handleClearAssignmentSelection}
                disabled={isSavingAllocation}
              >
                Wyczyść wybór
              </Button>
            </Box>

            {allocationError && <Alert severity="error">{allocationError}</Alert>}
            {allocationSuccess && <Alert severity="success">{allocationSuccess}</Alert>}
            <Status
              isLoading={isSavingAllocation}
              isError={isErrorSavingAllocation}
              isSuccess={isSuccessSavingAllocation}
            />
          </Box>
        </AccordionCard>
      </Box>

      <Box sx={{ mt: 3 }}>
        <AccordionCard
          title="Rezerwacje dla tego Bookingu"
          icon={<EventNoteIcon fontSize="small" color="primary" />}
          defaultExpanded
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={`Liczba miejsc: ${totalPlaces}`} color="primary" variant="outlined" />
            <Chip label={`Wolne miejsca: ${freePlaces}`} color={freePlacesChipColor} />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Wszystkie rezerwacje (uczestników) przypisane do tego bookingu.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => void handleDownloadReservationsPdf()}
              disabled={isLoadingReservations || isExportingReservationsPdf || reservationsForBooking.length === 0}
            >
              {isExportingReservationsPdf ? 'Generowanie PDF...' : 'Pobierz PDF'}
            </Button>
            <Tooltip title="Wystaw faktury dla zaznaczonych rezerwacji">
              <span>
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  startIcon={<ReceiptLongIcon />}
                  disabled={batchSelectedReservationIds.length === 0 || isBatchCreating}
                  onClick={() => setIsBatchModalOpen(true)}
                >
                  Wystaw FV dla wielu
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAddReservation}
              disabled={freePlaces <= 0 || isBookingCancelled}
            >
              Dodaj nową rezerwację
            </Button>
          </Box>
          <BookingReservationDataTable
            data={withReservations?.reservations || []}
            isLoading={isLoadingReservations}
            isError={false}
            errorMessage={undefined}
            totalCount={withReservations?.reservations?.length || 0}
            highlightedReservationId={focusedReservationId}
            applicantDisplayMode="summary"
            showPaymentSummary
            showCreateInvoiceAction
            onSelectionChange={setBatchSelectedReservationIds}
            onRowDeleted={() => {
              refetchReservationsForBooking();
            }}
          />

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Uczestnicy
            </Typography>
            <BookingParticipantsTable
              participants={allParticipants}
              isLoading={isLoadingReservations}
            />
          </Box>
        </AccordionCard>
      </Box>

      <Box sx={{ mt: 3 }}>
        <AccordionCard
          title="Rozliczenie księgowe"
          icon={<AccountBalanceWalletIcon fontSize="small" color="primary" />}
          defaultExpanded={false}
        >
          <BookingSettlementPanel bookingId={parsedBookingId} />
        </AccordionCard>
      </Box>

      <Box sx={{ mt: 3 }}>
        <AccordionCard
          title="Rozliczenie imprezy"
          icon={<SummarizeIcon fontSize="small" color="primary" />}
          defaultExpanded={false}
        >
          <BookingEventReportPanel bookingId={parsedBookingId} />
        </AccordionCard>
      </Box>

      <BookingSimplifiedCostModal
        open={isSimplifiedCostModalOpen}
        isSubmitting={isCreatingBookingSimplifiedCost}
        initialCompanyId={initialQuickCreateCompanyId}
        isCompanySelectionLocked={isQuickCreateCompanySelectionLocked}
        companies={simplifiedCostCompanies}
        tags={simplifiedCostTags}
        companiesLoading={isLoadingSimplifiedCostCompanies || isFetchingSimplifiedCostCompanies}
        tagsLoading={isLoadingSimplifiedCostTags || isFetchingSimplifiedCostTags}
        submitErrorMessage={quickCreateErrorState?.message ?? null}
        submitErrorCategory={quickCreateErrorState?.category ?? null}
        canManualRetry={canRetryQuickCreateManually}
        onClose={handleCloseSimplifiedCostModal}
        onSubmit={handleCreateBookingSimplifiedCost}
        onManualRetry={handleManualRetryBookingSimplifiedCost}
        onFallbackToExpenseInvoices={handleFallbackToExpenseInvoices}
      />
      {isBatchModalOpen && (
        <BatchInvoiceConfigModal
          open={isBatchModalOpen}
          onClose={() => setIsBatchModalOpen(false)}
          onConfirm={handleBatchModalConfirm}
          invoiceScheme={withReservations?.defaultInvoiceScheme}
          itemDescriptionPrefill={batchItemDescriptionPrefill}
          isLoading={isBatchCreating}
        />
      )}
    </EntityFormLayout>
  );
};

export default EditBooking;
